/**
 * Деньги: BIGINT в минимальных единицах валюты + ISO-код. См. DATABASE.md §8.
 * Все суммы в API и БД — целые числа minor units; здесь единственное место конвертации и форматирования.
 */

import type { Locale } from './locales';

export const CURRENCIES = {
  UZS: { exponent: 2, suffix: { ru: 'сум', uz: 'сўм' } },
  USD: { exponent: 2, suffix: { ru: '$', uz: '$' } },
  EUR: { exponent: 2, suffix: { ru: '€', uz: '€' } },
} as const;

export type CurrencyCode = keyof typeof CURRENCIES;

/** Валюты, которые сервер принимает в MVP. Расширяется в V1. */
export const SUPPORTED_CURRENCIES = ['UZS'] as const satisfies readonly CurrencyCode[];

/** 10^12 минимальных единиц (10 млрд сум) — верхняя граница, совпадает с CHECK в БД. */
export const MAX_AMOUNT_MINOR = 1_000_000_000_000;

export type Money = { amount: number; currency: string };

const NBSP = ' ';
const MINUS = '−';

export function isCurrencyCode(code: string): code is CurrencyCode {
  return Object.prototype.hasOwnProperty.call(CURRENCIES, code);
}

export function exponentOf(currency: string): number {
  return isCurrencyCode(currency) ? CURRENCIES[currency].exponent : 2;
}

/** Валидная сумма операции: целое, 1 ≤ n ≤ MAX. */
export function isValidAmountMinor(n: unknown): n is number {
  return typeof n === 'number' && Number.isSafeInteger(n) && n >= 1 && n <= MAX_AMOUNT_MINOR;
}

/**
 * Пользовательский ввод ("45 000", "45000", "12,5", "12.50") → minor units.
 * Без float: разбор строки по частям. Возвращает null, если ввод невалиден.
 */
export function parseToMinor(input: string | number, currency: string): number | null {
  const exp = exponentOf(currency);
  const raw = String(input)
    .replace(/[\s\u00A0\u202F_']/g, '')
    .replace(',', '.');
  if (!/^\d+(\.\d*)?$/.test(raw)) return null;
  const [intPart = '0', fracPartRaw = ''] = raw.split('.');
  if (fracPartRaw.length > exp) return null;
  const fracPart = fracPartRaw.padEnd(exp, '0');
  const minor = Number(intPart + fracPart);
  if (!Number.isSafeInteger(minor)) return null;
  return minor;
}

export function toMinor(major: number, currency: string): number {
  return Math.round(major * 10 ** exponentOf(currency));
}

export function fromMinor(minor: number, currency: string): number {
  return minor / 10 ** exponentOf(currency);
}

export type FormatMoneyOptions = {
  /** Показывать знак: '+' для положительных, '−' для отрицательных. Ноль — без знака. */
  withSign?: boolean;
  /** Добавлять суффикс валюты («сум» / «сўм»). */
  withCurrency?: boolean;
  /** Язык суффикса валюты; по умолчанию ru. */
  locale?: Locale;
};

/**
 * 4500000 UZS → "45 000"; -4000000 → "−40 000" (withSign); 1250 UZS → "12,50".
 * Дробная часть показывается только если она ненулевая (для UZS практически никогда).
 */
export function formatMoney(money: Money, opts: FormatMoneyOptions = {}): string {
  const exp = exponentOf(money.currency);
  const abs = Math.abs(money.amount);
  const base = 10 ** exp;
  const intPart = Math.floor(abs / base);
  const frac = abs % base;

  const intStr = intPart.toString().replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  const fracStr = frac === 0 ? '' : ',' + frac.toString().padStart(exp, '0');

  let sign = '';
  if (money.amount < 0) sign = MINUS;
  else if (opts.withSign && money.amount > 0) sign = '+';

  const suffix =
    opts.withCurrency && isCurrencyCode(money.currency)
      ? NBSP + CURRENCIES[money.currency].suffix[opts.locale ?? 'ru']
      : '';

  return `${sign}${intStr}${fracStr}${suffix}`;
}

export function moneyEquals(a: Money | null | undefined, b: Money | null | undefined): boolean {
  if (!a || !b) return a === b;
  return a.amount === b.amount && a.currency === b.currency;
}
